import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useLocation, useNavigate } from 'react-router-dom';
import MobileMenu from '@/components/MobileMenu';
import logo from '@/assets/logo.jpg';

const Navbar = () => {
  const { t, language, setLanguage } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: t.nav.services, href: '#services' },
    { label: t.nav.about, href: '#about' },
    { label: t.nav.tech, href: '#tech' },
    { label: t.nav.team, href: '#team' },
    { label: t.nav.blog, href: '#blog' },
    { label: t.nav.contact, href: '#contact' },
  ];

  const scrollToSection = (href: string) => {
    const el = document.getElementById(href.replace('#', ''));
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleBlogClick = () => { navigate('/blog'); };

  // Route links (real pages, not homepage anchors)
  const routeLinks = [
    { label: t.nav.cx, to: '/cx' },
  ];

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (isHomePage) {
      scrollToSection(href);
    } else {
      navigate('/');
      setTimeout(() => scrollToSection(href), 150);
    }
    setMobileOpen(false);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <>
      <nav className={`site-nav${scrolled ? ' scrolled' : ''}`}>
        <a href="/" className="nav-logo" onClick={handleLogoClick}>
          <img src={logo} alt="ADD Individual Solutions" width={86} height={40} style={{ height: '40px', objectFit: 'contain' }} />
        </a>

        <div className="nav-links">
          {navLinks.map((link) => (
            <a key={link.href} href={isHomePage ? link.href : `/${link.href}`} onClick={(e) => handleNav(e, link.href)}>
              {link.label}
            </a>
          ))}
          {routeLinks.map((link) => (
            <a
              key={link.to}
              href={link.to}
              onClick={(e) => { e.preventDefault(); navigate(link.to); setMobileOpen(false); }}
              style={location.pathname.startsWith(link.to) ? { color: 'var(--blue)' } : undefined}
            >
              {link.label}
            </a>
          ))}
          <a href="/blog" onClick={(e) => { e.preventDefault(); handleBlogClick(); }} style={{ color: 'var(--amber)', fontWeight: 700 }}>
            Blog
          </a>
        </div>

        <div className="nav-actions">
          <div className="lang-sw">
            <button className={language === 'en' ? 'on' : ''} onClick={() => setLanguage('en')}>EN</button>
            <button className={language === 'ro' ? 'on' : ''} onClick={() => setLanguage('ro')}>RO</button>
          </div>
          <a href={isHomePage ? '#contact' : '/#contact'} onClick={(e) => handleNav(e, '#contact')} className="nav-cta">
            {t.nav.getInTouch}
          </a>
        </div>

        <button className="nav-ham" onClick={() => setMobileOpen(true)}>
          <span /><span /><span />
        </button>
      </nav>

      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        links={navLinks}
        routeLinks={routeLinks}
        onNavigate={handleNav}
        language={language}
        setLanguage={setLanguage}
        isHomePage={isHomePage}
      />
    </>
  );
};

export default Navbar;
